/**
 * src/components/Financial/useAstroData.js
 * パフォーマンス最適化・計算キャッシュ導入版
 */
import { useMemo } from "react";
import * as Astro from "../../astronomy.js";
const getExtendedPoints = Astro.getExtendedPoints;
import {
  PLANET_SYMBOLS,
  PLANETS,
  PLANET_COLORS,
} from "../../utils/constants.js";

export function useAstroData(params) {
  // 基本的な座標計算関数
  var getPosAtRaw = function (date) {
    var p = params.isHeliocentric
      ? Astro.getAllHeliocentricLongitudes(date)
      : Astro.getAllPlanetLongitudesExtended(
          date,
          params.observerLat,
          params.observerLon,
        );

    if (!params.isHeliocentric) {
      var ext = getExtendedPoints(date);
      p = Object.assign({}, p, ext);
    }

    if (params.isSidereal) {
      var ayan = 23.8528;
      var keys = Object.keys(p);
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        if (typeof p[k] === "number") {
          p[k] = (p[k] - ayan + 360) % 360;
        }
      }
    }
    return p;
  };

  return useMemo(
    function () {
      var res = {
        retrogrades: {},
        ingresses: [],
        lunations: [],
        gannLevels: [],
        squaringMarkers: [],
      };

      if (
        !params.fullTimeline ||
        params.fullTimeline.length === 0 ||
        !params.stats ||
        !params.aggregatedData
      ) {
        return res;
      }

      // ★ 修正ポイント1: astronomy.js の高精度関数を使用して全期間のイングレスを取得
      if (params.selectedIngressPlanets.size > 0) {
        const allIngresses = Astro.getIngressesForTimeline(
          params.fullTimeline,
          params.isHeliocentric,
          params.isSidereal,
        );

        // ユーザーが選択した惑星のみにフィルタリングして結果に格納
        res.ingresses = allIngresses.filter(function (ev) {
          return params.selectedIngressPlanets.has(ev.planet);
        });
      }

      // --- キャッシュ機構の導入 ---
      var posCache = new Map();
      var getCachedPos = function (ts) {
        if (posCache.has(ts)) return posCache.get(ts);
        var p = getPosAtRaw(new Date(ts));
        posCache.set(ts, p);
        return p;
      };

      var effectiveStart = Math.min(
        params.startIndex,
        Math.max(0, params.fullTimeline.length - params.visibleCount),
      );
      var visibleSlice = params.fullTimeline.slice(
        effectiveStart,
        effectiveStart + params.visibleCount,
      );
      var scaleVal = parseFloat(params.gannScale);
      var minP = params.stats.min;
      var maxP = params.stats.max;

      // 1. Gann & Planetary Lines (基準日時点の計算)
      if (params.gannBasePrice && scaleVal > 0) {
        var rootBase = Math.sqrt(parseFloat(params.gannBasePrice) * scaleVal);
        for (var off = -60; off <= 60; off++) {
          var angle = off * 45;
          var price = Math.pow(rootBase + angle / 180, 2) / scaleVal;
          if (price >= minP && price <= maxP) {
            res.gannLevels.push({
              type: "fixed",
              angle: angle,
              price: price,
              color: angle % 90 === 0 ? "#27ae60" : "#95a5a6",
            });
          }
        }
      }

      if (params.selectedGannPlanets.size > 0) {
        var lastActualIdx = params.aggregatedData.length - 1;
        var targetTs =
          params.aggregatedData[lastActualIdx >= 0 ? lastActualIdx : 0]
            .timestamp;
        var posNow = getCachedPos(targetTs);
        params.selectedGannPlanets.forEach(function (p) {
          if (posNow[p] === undefined || posNow[p] === null) return;
          var rMin = Math.sqrt(minP * scaleVal);
          var rMax = Math.sqrt(maxP * scaleVal);
          for (var c = Math.floor(rMin) - 1; c <= Math.ceil(rMax) + 1; c++) {
            var pPrice = Math.pow(c + posNow[p] / 180, 2) / scaleVal;
            if (pPrice >= minP && pPrice <= maxP) {
              res.gannLevels.push({
                type: "planet",
                planet: p,
                price: pPrice,
                color: PLANET_COLORS[p],
                symbol: PLANET_SYMBOLS[p],
                angle: Math.floor(posNow[p] % 360),
              });
            }
          }
        });
      }

      // 2. イベントスキャン
      var prevPh = null;
      if (visibleSlice.length > 0) {
        var prePos = getCachedPos(visibleSlice[0].timestamp - 86400000);
        prevPh = (prePos.Moon - prePos.Sun + 360) % 360;
      }

      visibleSlice.forEach(function (d, lIdx) {
        var gIdx = effectiveStart + lIdx;
        var ts = d.timestamp;
        var pos = getCachedPos(ts);

        // ルネーション
        if (params.showLunations === true && !params.isHeliocentric) {
          var ph = (pos.Moon - pos.Sun + 360) % 360;
          if (prevPh !== null && (ph < prevPh || (prevPh < 180 && ph >= 180))) {
            var isNew = ph < prevPh;
            var nodePos = pos.TrueNode || pos.Ascnode || 0;
            var nodeDist = Math.min(
              Math.abs(pos.Moon - nodePos) % 180,
              180 - (Math.abs(pos.Moon - nodePos) % 180),
            );
            var lLabel = isNew ? "New Moon" : "Full Moon";
            var lColor = isNew ? "#475569" : "#cbd5e1";
            if (isNew && nodeDist < 18.5) {
              lLabel += " (Solar Eclipse)";
              lColor = "#f59e0b";
            } else if (!isNew && nodeDist < 12.2) {
              lLabel += " (Lunar Eclipse)";
              lColor = "#ef4444";
            }
            res.lunations.push({
              index: gIdx,
              symbol: "●",
              label: lLabel,
              date: d.Date,
              color: lColor,
            });
          }
          prevPh = ph;
        }

        // 逆行
        if (params.selectedRetroPlanets.size > 0) {
          var posNextRet = getCachedPos(ts + 86400000);
          params.selectedRetroPlanets.forEach(function (p) {
            // 翌日の経度が前日より減少していれば逆行と判定
            if (((posNextRet[p] - pos[p] + 540) % 360) - 180 < 0) {
              if (!res.retrogrades[p]) res.retrogrades[p] = [];
              res.retrogrades[p].push(gIdx);
            }
          });
        }
      });
      return res;
    },
    [
      params.fullTimeline,
      params.startIndex,
      params.visibleCount,
      params.timeframe,
      params.stats,
      params.aggregatedData,
      params.isHeliocentric,
      params.isSidereal,
      params.observerLat,
      params.observerLon,
      params.selectedIngressPlanets,
      params.selectedGannPlanets,
      params.selectedRetroPlanets,
      params.gannBasePrice,
      params.gannScale,
      params.showLunations,
      params.showSquaringMarkers,
    ],
  );
}
